import { Box, Stack, Tab, Tabs } from "@mui/material";
import { useCardTransactionsAvailability } from "Api/Queries/CardTransactions/useCardTransactionsAvailability";
import { useFilteredTransactions } from "Api/Queries/Transactions/useFilteredTransactions";
import { useSearchTransactions } from "Api/Queries/Transactions/useSearchTransactions";
import { useTransactionsSummary } from "Api/Queries/Transactions/useTransactionsSummary";
import { CardTransactionsTabContent } from "Components/CardTransactions/CardTransactionsTabContent";
import { TransactionsList } from "Components/Shared/Transactions/TransactionsList/TransactionsList";
import { TransactionsSummaryBox } from "Components/Transactions/TransactionsSummaryBox";
import { useMemo } from "react";
import { TransactionFilterChips } from "Components/Transactions/TransactionsSearchBar/TransactionFilters/TransactionFilterChips";
import { TransactionFilterBar } from "Components/Transactions/TransactionsSearchBar/TransactionsFilterBar";
import { useTransactionFilterManager } from "Components/Transactions/hooks/useTransactionFilterManager";
import { PendingInverseTransactions } from "Components/Transactions/PendingInverseTransactions";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { useSearchParams } from "Navigation/useSearchParams";
import { Resources, useResource } from "Translations/Resources";
import { getRegionCurrency } from "Utils/CurrencyUtils";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";

const TABS_RESOURCES = Resources.Components.Transactions.Tabs;
const TRANSFERS_TAB = "transfers";
const CARDS_TAB = "cards";
const TABS_ID_PREFIX = "merchant-transactions";

type Props = {
  initialLimit?: number;
};

export const MerchantTransactionsPageContent = ({
  initialLimit = 10,
}: Props) => {
  const { t } = useResource();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useLoggedInUser();

  const filterManager = useTransactionFilterManager({
    variant: "merchant",
    storageKey: LocalStorageKeys.TransactionFilters,
  });

  const { filters, availableFilters, searchPublicID, activeFilterCount } =
    filterManager;
  const isSearching = !!searchPublicID;

  // FR-UI-6: the Cards tab only exists for merchants with card acceptance (OrbitL + paired terminal, or history).
  const { data: cardsAvailability, isPending: isCardsAvailabilityPending } =
    useCardTransactionsAvailability();
  const isCardsTabAvailable = cardsAvailability?.isAvailable ?? false;

  // Honour a ?tab=cards deep link while availability is still loading — flashing the Transfers content only to
  // switch a moment later reads as a glitch. An ineligible merchant falls back to Transfers once availability
  // resolves; without the deep link nothing renders differently while pending.
  const requestsCardsTab = searchParams.get("tab") === CARDS_TAB;
  const isCardsTab =
    requestsCardsTab && (isCardsTabAvailable || isCardsAvailabilityPending);
  const activeTab = isCardsTab ? CARDS_TAB : TRANSFERS_TAB;
  const isCardsTabVisible = isCardsTabAvailable || isCardsTab;

  // Transfers data is only shown on the Transfers tab — don't fetch (or poll) it while the Cards tab is active.
  // Summary - only uses filters (not search), disabled when searching
  const { data: summaryData, isLoading: isLoadingSummary } =
    useTransactionsSummary({ filters, enabled: !isSearching && !isCardsTab });

  // Filtered transactions - used when NOT searching
  const filteredTransactionsQuery = useFilteredTransactions({
    filters,
    limit: initialLimit,
    enabled: !isCardsTab,
  });

  // Search transactions - used when searching (ignores filters)
  const searchQuery = useSearchTransactions({
    searchPublicID,
    limit: initialLimit,
    enabled: !isCardsTab,
  });

  // Select which query results to display
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
    isSearching ? searchQuery : filteredTransactionsQuery;

  const transactions = useMemo(
    () => data?.pages?.flatMap(({ items }) => items) ?? [],
    [data],
  );

  const totalCount = data?.pages?.[0]?.totalCount ?? 0;

  const handleTabChange = (tab: string) => {
    // Merge instead of replace — passing a plain object would wipe unrelated query params.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    });
  };

  return (
    <Stack gap={3}>
      <Tabs
        value={activeTab}
        onChange={(_e, tab: string) => handleTabChange(tab)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label={t(TABS_RESOURCES.AriaLabel)}
      >
        <Tab
          id={`${TABS_ID_PREFIX}-tab-${TRANSFERS_TAB}`}
          aria-controls={`${TABS_ID_PREFIX}-tabpanel-${TRANSFERS_TAB}`}
          label={t(TABS_RESOURCES.Transfers)}
          value={TRANSFERS_TAB}
        />
        {isCardsTabVisible && (
          <Tab
            id={`${TABS_ID_PREFIX}-tab-${CARDS_TAB}`}
            aria-controls={`${TABS_ID_PREFIX}-tabpanel-${CARDS_TAB}`}
            label={t(TABS_RESOURCES.Cards)}
            value={CARDS_TAB}
          />
        )}
      </Tabs>
      <div
        role="tabpanel"
        id={`${TABS_ID_PREFIX}-tabpanel-${TRANSFERS_TAB}`}
        aria-labelledby={`${TABS_ID_PREFIX}-tab-${TRANSFERS_TAB}`}
        hidden={isCardsTab}
      >
        {!isCardsTab && (
          <Stack gap={3}>
            <TransactionsSummaryBox
              filters={filters}
              isLoading={isLoadingSummary}
              totalSums={summaryData?.totalSums ?? []}
              emptyStateCurrency={
                currentUser?.region
                  ? getRegionCurrency(currentUser.region)
                  : undefined
              }
            />
            <PendingInverseTransactions />
            <TransactionFilterChips
              filters={filters}
              availableFilters={availableFilters}
              isVisible={activeFilterCount > 0}
            />
            <TransactionFilterBar filterManager={filterManager} />
            <Box mt={2}>
              <TransactionsList
                variant="merchant"
                initialLimit={initialLimit}
                transactions={transactions}
                totalCount={totalCount}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
                isLoadingTransactions={isLoading}
                skeletonsCount={isSearching ? 0 : undefined}
              />
            </Box>
          </Stack>
        )}
      </div>
      <div
        role="tabpanel"
        id={`${TABS_ID_PREFIX}-tabpanel-${CARDS_TAB}`}
        aria-labelledby={`${TABS_ID_PREFIX}-tab-${CARDS_TAB}`}
        hidden={!isCardsTab}
      >
        {isCardsTab && (
          <CardTransactionsTabContent initialLimit={initialLimit} />
        )}
      </div>
    </Stack>
  );
};
